import type { StoreLimitUpgradeTarget, StoreTrialEligibilityState } from '@common/api/authService';
import type { Translate } from '@client-app/i18n';
import {
    getPricingTierIntegrationConnections,
    getPricingTierLimits,
    getPricingTiers,
} from '@common/config/pricingCatalog';

export interface ConnectRestrictionPayload {
    restrictionState?: StoreTrialEligibilityState | null;
    eligibilityState?: StoreTrialEligibilityState | null;
    blockConnect?: boolean | null;
    upgradeRequired?: boolean | null;
    message?: string | null;
    eligibilityMessage?: string | null;
    allowsFreshTrial?: boolean | null;
    trialRemainingDays?: number | null;
    currentWorkspaceId?: string | null;
    currentWorkspaceName?: string | null;
    currentStoreCount?: number | null;
    storeLimit?: number | null;
    upgradeTarget?: StoreLimitUpgradeTarget | null;
}

export interface NormalizedConnectRestriction {
    restrictionState: StoreTrialEligibilityState | null;
    blockConnect: boolean;
    upgradeRequired: boolean;
    message: string | null;
    allowsFreshTrial: boolean | null;
    trialRemainingDays: number | null;
    currentWorkspaceId: string | null;
    currentWorkspaceName: string | null;
    currentStoreCount: number | null;
    storeLimit: number | null;
    upgradeTarget: StoreLimitUpgradeTarget | null;
}

export const normalizeConnectRestriction = (
    payload?: ConnectRestrictionPayload | null
): NormalizedConnectRestriction => {
    const restrictionState = payload?.restrictionState ?? payload?.eligibilityState ?? null;
    const allowsFreshTrial = payload?.allowsFreshTrial ?? null;

    return {
        restrictionState,
        blockConnect: payload?.blockConnect ?? false,
        upgradeRequired: payload?.upgradeRequired ?? false,
        message: payload?.message ?? payload?.eligibilityMessage ?? null,
        allowsFreshTrial,
        trialRemainingDays: payload?.trialRemainingDays ?? null,
        currentWorkspaceId: payload?.currentWorkspaceId ?? null,
        currentWorkspaceName: payload?.currentWorkspaceName ?? null,
        currentStoreCount: payload?.currentStoreCount ?? null,
        storeLimit: payload?.storeLimit ?? null,
        upgradeTarget: payload?.upgradeTarget ?? null,
    };
};

export interface StoreLimitPrompt {
    title: string;
    description: string;
    primaryActionLabel: string;
    secondaryActionLabel: string;
    upgradeTarget: StoreLimitUpgradeTarget | null;
}

export interface ConnectRestrictionCopyContext {
    planName: 'Solo' | 'Team' | null;
    storeLimit: number | null;
    otherIntegrationLimit: number | null;
    totalIntegrationLimit: number | null;
}

const EMPTY_CONNECT_RESTRICTION_COPY_CONTEXT: ConnectRestrictionCopyContext = {
    planName: null,
    storeLimit: null,
    otherIntegrationLimit: null,
    totalIntegrationLimit: null,
};

export const resolveConnectRestrictionCopyContext = (
    planCode?: string | null,
): ConnectRestrictionCopyContext => {
    const normalizedPlanCode = String(planCode || '').trim().toUpperCase();
    const tier = [...getPricingTiers('solo'), ...getPricingTiers('team')]
        .find((candidate) => candidate.planCode === normalizedPlanCode);

    if (!tier) {
        return EMPTY_CONNECT_RESTRICTION_COPY_CONTEXT;
    }

    const limits = getPricingTierLimits(tier, 'monthly');
    const integrationLimits = getPricingTierIntegrationConnections(tier);

    return {
        planName: tier.group === 'solo' ? 'Solo' : 'Team',
        storeLimit: typeof limits.storeConnections === 'number' ? limits.storeConnections : null,
        otherIntegrationLimit: typeof integrationLimits.otherIntegrations === 'number'
            ? integrationLimits.otherIntegrations
            : null,
        totalIntegrationLimit: typeof integrationLimits.total === 'number'
            ? integrationLimits.total
            : null,
    };
};

export interface ConnectRestrictionAction {
    shouldBlockConnect: boolean;
    shouldOpenPricing: boolean;
    shouldShowSubscriptionPrompt: boolean;
    shouldShowStoreLimitPrompt: boolean;
    message: string | null;
    restrictionState: StoreTrialEligibilityState | null;
    currentStoreCount: number | null;
    storeLimit: number | null;
    upgradeTarget: StoreLimitUpgradeTarget | null;
    storeLimitPrompt: StoreLimitPrompt | null;
}

const SUBSCRIPTION_PROMPT_RESTRICTION_STATES: StoreTrialEligibilityState[] = [
    'already_bound',
    'trial_expired',
    'trial_valid',
];

const LIMIT_PROMPT_RESTRICTION_STATES: StoreTrialEligibilityState[] = [
    'store_limit_reached',
    'integration_group_limit_reached',
    'total_connection_limit_reached',
];

const buildStoreLimitPrompt = (
    normalized: NormalizedConnectRestriction,
    t: Translate,
    copyContext: ConnectRestrictionCopyContext,
): StoreLimitPrompt => {
    const isSalesTarget = normalized.upgradeTarget === 'SALES';
    const primaryActionLabel = isSalesTarget
        ? t('website.pricing.contactSales')
        : t('website.pricing.viewPlans');

    if (normalized.restrictionState === 'integration_group_limit_reached') {
        const hasKnownLimit = Boolean(copyContext.planName && copyContext.otherIntegrationLimit);
        return {
            title: t('website.integrationsSettings.otherIntegrationConnectionLimitReached'),
            description: hasKnownLimit
                ? t(isSalesTarget
                    ? 'website.integrationsSettings.otherIntegrationLimitSalesDescription'
                    : 'website.integrationsSettings.otherIntegrationLimitPlansDescription', {
                    plan: copyContext.planName,
                    limit: copyContext.otherIntegrationLimit,
                })
                : t(isSalesTarget
                    ? 'website.integrationsSettings.otherIntegrationLimitSalesFallbackDescription'
                    : 'website.integrationsSettings.otherIntegrationLimitPlansFallbackDescription'),
            primaryActionLabel,
            secondaryActionLabel: t('website.integrationsSettings.notNow'),
            upgradeTarget: normalized.upgradeTarget,
        };
    }

    if (normalized.restrictionState === 'total_connection_limit_reached') {
        const hasKnownLimit = Boolean(copyContext.planName && copyContext.totalIntegrationLimit);
        return {
            title: t('website.integrationsSettings.totalIntegrationConnectionLimitReached'),
            description: hasKnownLimit
                ? t(isSalesTarget
                    ? 'website.integrationsSettings.totalIntegrationLimitSalesDescription'
                    : 'website.integrationsSettings.totalIntegrationLimitPlansDescription', {
                    plan: copyContext.planName,
                    limit: copyContext.totalIntegrationLimit,
                })
                : t(isSalesTarget
                    ? 'website.integrationsSettings.totalIntegrationLimitSalesFallbackDescription'
                    : 'website.integrationsSettings.totalIntegrationLimitPlansFallbackDescription'),
            primaryActionLabel,
            secondaryActionLabel: t('website.integrationsSettings.notNow'),
            upgradeTarget: normalized.upgradeTarget,
        };
    }

    const storeLimit = normalized.storeLimit ?? copyContext.storeLimit;
    const hasKnownLimit = Boolean(copyContext.planName && storeLimit);
    const storeLimitLabel = storeLimit
        ? t('website.integrationsSettings.connectedStoreCount', {
            count: storeLimit,
            plural: storeLimit === 1 ? '' : 's',
        })
        : null;

    return {
        title: t('website.integrationsSettings.storeConnectionLimitReached'),
        description: hasKnownLimit
            ? t(isSalesTarget
                ? 'website.integrationsSettings.storeLimitSalesDescription'
                : 'website.integrationsSettings.storeLimitPlansDescription', {
                plan: copyContext.planName,
                limit: storeLimitLabel,
            })
            : t(isSalesTarget
                ? 'website.integrationsSettings.storeLimitSalesFallbackDescription'
                : 'website.integrationsSettings.storeLimitPlansFallbackDescription'),
        primaryActionLabel,
        secondaryActionLabel: t('website.integrationsSettings.notNow'),
        upgradeTarget: normalized.upgradeTarget,
    };
};

export const getConnectRestrictionAction = (
    payload?: ConnectRestrictionPayload | null,
    t?: Translate,
    copyContext: ConnectRestrictionCopyContext = EMPTY_CONNECT_RESTRICTION_COPY_CONTEXT,
): ConnectRestrictionAction => {
    const normalized = normalizeConnectRestriction(payload);
    const shouldShowStoreLimitPrompt = normalized.blockConnect
        && normalized.restrictionState !== null
        && LIMIT_PROMPT_RESTRICTION_STATES.includes(normalized.restrictionState);
    const shouldShowSubscriptionPrompt = normalized.blockConnect && !shouldShowStoreLimitPrompt && (
        normalized.upgradeRequired
        || (
            normalized.restrictionState !== null
            && SUBSCRIPTION_PROMPT_RESTRICTION_STATES.includes(normalized.restrictionState)
        )
    );

    return {
        shouldBlockConnect: normalized.blockConnect,
        shouldOpenPricing: normalized.blockConnect
            && normalized.upgradeRequired
            && normalized.upgradeTarget !== 'SALES',
        shouldShowSubscriptionPrompt,
        shouldShowStoreLimitPrompt,
        message: normalized.message,
        restrictionState: normalized.restrictionState,
        currentStoreCount: normalized.currentStoreCount,
        storeLimit: normalized.storeLimit,
        upgradeTarget: normalized.upgradeTarget,
        storeLimitPrompt: shouldShowStoreLimitPrompt && t
            ? buildStoreLimitPrompt(normalized, t, copyContext)
            : null,
    };
};
